<script lang="ts">
import type { DropdownMenuContentEmits as RekaDropdownMenuContentEmits, DropdownMenuContentProps as RekaDropdownMenuContentProps } from 'reka-ui'
import type { ArrayOrNested } from '../types/utils'
import type { DropdownMenuItem } from './AwDropdownMenu.vue'

interface DropdownMenuContentProps<T extends ArrayOrNested<DropdownMenuItem>> extends Omit<RekaDropdownMenuContentProps, 'as' | 'asChild' | 'forceMount'> {
  items?: T
  portal?: boolean | string | HTMLElement
  sub?: boolean
  class?: string | object
}

interface DropdownMenuContentEmits extends RekaDropdownMenuContentEmits {}

export interface DropdownMenuContentSlots {
  default?: () => any
}

const ui = {
  viewport: 'relative overflow-y-auto flex-1',
  group: 'py-0.5 isolate',
}
</script>

<script setup lang="ts" generic="T extends ArrayOrNested<DropdownMenuItem>">
import { reactiveOmit } from '@vueuse/core'
import { useForwardPropsEmits } from 'reka-ui'
import { DropdownMenu } from 'reka-ui/namespaced'
import { computed, toRef } from 'vue'
import { usePortal } from '../composables/usePortal'
import { isArrayOfArray } from '../utils'
import AwListItem from './AwListItem.vue'
import AwDropdownMenuContent from './DropdownMenuContent.vue'

const props = defineProps<DropdownMenuContentProps<T>>()
const emits = defineEmits<DropdownMenuContentEmits>()

const slots = defineSlots<DropdownMenuContentSlots>()

const portalProps = usePortal(toRef(() => props.portal))
const contentProps = useForwardPropsEmits(reactiveOmit(props, 'sub', 'items', 'portal', 'class'), emits)

const groups = computed<DropdownMenuItem[][]>(() =>
  props.items?.length
    ? isArrayOfArray(props.items)
      ? props.items
      : [props.items]
    : [],
)
</script>

<template>
  <DropdownMenu.Portal v-bind="portalProps">
    <component :is="sub ? DropdownMenu.SubContent : DropdownMenu.Content" :class="props.class" v-bind="contentProps">
      <div role="presentation" :class="ui.viewport">
        <DropdownMenu.Group v-for="(group, groupIndex) in groups" :key="`group-${groupIndex}`" :class="ui.group" @click.prevent>
          <template v-for="(item, index) in group" :key="`group-${groupIndex}-${index}`">
            <AwListItem
              v-if="!item.children?.length" v-bind="item" :as="item.type === 'label'
                ? DropdownMenu.Label : item.type === 'separator'
                  ? DropdownMenu.Separator : DropdownMenu.Item"
            />
            <DropdownMenu.Sub v-else :open="item.open" :default-open="item.defaultOpen">
              <AwListItem v-bind="item" />
              <AwDropdownMenuContent
                sub
                :class="props.class"
                :portal="portal"
                :items="(item.children as T)"
                align="start"
                :align-offset="-5"
                :side-offset="3"
              />
            </DropdownMenu.Sub>
          </template>
        </DropdownMenu.Group>
        <slot />
      </div>
    </component>
  </DropdownMenu.Portal>
</template>
